/**
 * Product detail (build item 13, docs/plan.md → Screens, "Product detail").
 * hideShellChrome per the design's `isProduct` branch — `ProductDetailView`
 * renders its own back/Edit bar, full-bleed, matching `/leads/:id`.
 *
 * Resolves the product by `itemNo` from the offline-first Dexie catalogue
 * mirror (`useProductByItemNo` → `useCatalogueProducts`,
 * lib/catalogue-data.ts) rather than a dedicated network fetch, so this
 * screen works from a cold start with no signal exactly like `/products`
 * does — the mirror is already kept fresh by the app-wide pull in
 * lib/offline, and `useCatalogueProducts` itself kicks one extra
 * revalidation whenever a catalogue screen mounts online.
 */
import { createFileRoute, Link } from '@tanstack/react-router';

import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { ProductDetailView } from '@/components/product-mgmt/product-detail-view';
import { useProductByItemNo } from '@/components/product-mgmt/use-product-by-item-no';
import { useIsOffline } from '@/lib/offline';

export const Route = createFileRoute('/_app/products/$itemNo/')({
  staticData: { hideShellChrome: true },
  component: ProductDetailRoute
});

function ProductDetailRoute() {
  const { itemNo } = Route.useParams();
  const numericItemNo = Number(itemNo);
  const { product, loading } = useProductByItemNo(numericItemNo);
  const isOffline = useIsOffline();

  // Spinner while the mirror resolves — don't flash "not found" during the
  // post-reload window (see use-product-by-item-no.ts).
  if (loading) {
    return (
      <div className="grid h-dvh place-items-center p-6">
        <Spinner className="size-5" aria-hidden="true" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="grid h-dvh place-items-center p-6">
        <div className="flex flex-col items-center gap-3 text-center">
          <p className="text-sm text-muted-foreground">
            This product couldn't be found{isOffline ? ' — it may not have synced to this device yet.' : '.'}
          </p>
          <Button render={<Link to="/products" />} variant="outline">
            ‹ Back to Products
          </Button>
        </div>
      </div>
    );
  }

  return <ProductDetailView product={product} />;
}
